
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/context/LanguageContext";
import CookieConsent from "@/components/CookieConsent";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import SignupComplete from "./pages/SignupComplete";
import SignupVerifyNotice from "./pages/SignupVerifyNotice";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Profile from "./pages/Profile";
import ProfileEdit from "./pages/ProfileEdit";
import Reviews from "./pages/Reviews";
import ReviewDetail from "./pages/ReviewDetail";
import ReviewEdit from "./pages/ReviewEdit";
import MemberReviews from "./pages/MemberReviews";
import AddressDetail from "./pages/AddressDetail";
import Submit from "./pages/Submit";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import Guides from "./pages/Guides";
import GuideDetail from "./pages/GuideDetail";
import Points from "./pages/Points";
import RedemptionHistory from "./pages/RedemptionHistory";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import NotFound from "./pages/NotFound";
import VerifyPage from "./pages/verify";

// Admin pages
import AdminLogin from "./pages/admin/AdminLogin";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminLayout from "./components/admin/Layout";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminUsers from "./pages/admin/Users";
import AdminReviews from "./pages/admin/Reviews";
import AdminReviewEdit from "./pages/admin/ReviewEdit";
import AdminArticles from "./pages/admin/Articles";
import AdminGuides from "./pages/admin/Guides";
import AdminSettings from "./pages/admin/Settings";
import AdminRewardManagement from "./pages/admin/RewardManagement";
import AdminRedemptionHistory from "./pages/admin/RedemptionHistory";
import AdminBadgeManagement from "./pages/admin/BadgeManagement";
import AdminMemberLevels from "./pages/admin/MemberLevels";
import AdminPointsSettings from "./pages/admin/PointsSettings";

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <CookieConsent />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/signup/complete" element={<SignupComplete />} />
              <Route path="/signup-complete" element={<SignupComplete />} />
              <Route path="/signup-verify-notice" element={<SignupVerifyNotice />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/profile/edit" element={<ProfileEdit />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/reviews/:id" element={<ReviewDetail />} />
              <Route path="/reviews/:id/edit" element={<ReviewEdit />} />
              <Route path="/member-reviews" element={<MemberReviews />} />
              <Route path="/address/:addressId" element={<AddressDetail />} />
              <Route path="/submit" element={<Submit />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:id" element={<ArticleDetail />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/guides/:id" element={<GuideDetail />} />
              <Route path="/points" element={<Points />} />
              <Route path="/redemption-history" element={<RedemptionHistory />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              
              {/* Admin Routes - Login first, then protected admin routes */}
              <Route path="/jay" element={<AdminLogin />} />
              <Route path="/jay/*" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="reviews/:id/edit" element={<AdminReviewEdit />} />
                <Route path="articles" element={<AdminArticles />} />
                <Route path="guides" element={<AdminGuides />} />
                <Route path="levels" element={<AdminMemberLevels />} />
                <Route path="badges" element={<AdminBadgeManagement />} />
                <Route path="rewards" element={<AdminRewardManagement />} />
                <Route path="redemptions" element={<AdminRedemptionHistory />} />
                <Route path="points-settings" element={<AdminPointsSettings />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
